import dicomImageLoader from '@cornerstonejs/dicom-image-loader';
import { DicomMetadataStore } from '@ohif/core';
import type { ImportStudyInstanceMetadata, ImportStudyNaturalizedDataset } from './parseImportStudyDicomFiles';
import {
  notifyImportedStudiesAdded,
} from './importStudySession';
import { useImportedStudyStore } from '../../stores/useImportedStudyStore';

const DICOM_LOCAL_DATA_SOURCE = 'dicomlocal';

function getTransferSyntaxUID(dataset: ImportStudyNaturalizedDataset): string | undefined {
  if (typeof dataset.AvailableTransferSyntaxUID === 'string') {
    return dataset.AvailableTransferSyntaxUID;
  }

  const transferSyntax = dataset._meta?.TransferSyntaxUID as { Value?: string[] } | undefined;
  return transferSyntax?.Value?.[0];
}

function prepareDatasetForMetadataStore(
  instance: ImportStudyInstanceMetadata
): ImportStudyNaturalizedDataset {
  const imageId = dicomImageLoader.wadouri.fileManager.add(instance.file);
  const dataset = { ...instance.dataset };
  dataset.url = imageId;
  dataset.imageId = imageId;
  dataset.isImported = true;
  dataset.AvailableTransferSyntaxUID = getTransferSyntaxUID(dataset);
  return dataset;
}

/**
 * Registers a parsed file with DicomMetadataStore. When the same SOPInstanceUID
 * is already present (e.g. study was loaded from PACS then re-imported from a
 * download), addInstance dedupes and keeps the old entry — which has no local
 * `url`. Update the existing instance so dicomlocal can resolve wadouri imageIds.
 */
function registerImportedInstance(instance: ImportStudyInstanceMetadata): void {
  const dataset = prepareDatasetForMetadataStore(instance);
  const { StudyInstanceUID, SeriesInstanceUID, SOPInstanceUID } = dataset;

  if (!StudyInstanceUID || !SeriesInstanceUID || !SOPInstanceUID) {
    DicomMetadataStore.addInstance(dataset);
    return;
  }

  const existingInstance = DicomMetadataStore.getInstance(
    StudyInstanceUID,
    SeriesInstanceUID,
    SOPInstanceUID
  );

  if (existingInstance) {
    existingInstance.url = dataset.url;
    existingInstance.imageId = dataset.url;
    existingInstance.isImported = true;

    if (dataset.AvailableTransferSyntaxUID) {
      existingInstance.AvailableTransferSyntaxUID = dataset.AvailableTransferSyntaxUID;
    }

    return;
  }

  DicomMetadataStore.addInstance(dataset);
}

function summarizeStudy(instances: ImportStudyInstanceMetadata[]) {
  const firstInstance = instances[0];
  const modalities = [...new Set(instances.map(instance => instance.modality).filter(Boolean))];

  return {
    studyInstanceUID: firstInstance.studyInstanceUID,
    studyDate: firstInstance.studyDate,
    studyDescription: firstInstance.studyDescription || 'Imported Study',
    modalities: modalities.join('/'),
    numInstances: instances.length,
    patientName: firstInstance.patientName,
  };
}

/**
 * Registers parsed DICOM files with the in-memory metadata store and triggers
 * OHIF display set creation through the dicomlocal data source API.
 */
export async function loadImportedStudyIntoViewer(
  instances: ImportStudyInstanceMetadata[],
  extensionManager: AppTypes.ExtensionManager
): Promise<string[]> {
  const localDataSource = extensionManager.getDataSourceInstance(DICOM_LOCAL_DATA_SOURCE);

  if (!localDataSource?.retrieve?.series?.metadata) {
    throw new Error('dicomlocal data source is not configured');
  }

  for (const instance of instances) {
    registerImportedInstance(instance);
  }

  const instancesByStudy = instances.reduce<Map<string, ImportStudyInstanceMetadata[]>>(
    (map, instance) => {
      const studyInstances = map.get(instance.studyInstanceUID) ?? [];
      studyInstances.push(instance);
      map.set(instance.studyInstanceUID, studyInstances);
      return map;
    },
    new Map()
  );

  const studyInstanceUIDs = [...instancesByStudy.keys()];
  const studiesToRegister = [];

  for (const [studyInstanceUID, studyInstances] of instancesByStudy) {
    studiesToRegister.push(summarizeStudy(studyInstances));

    await localDataSource.retrieve.series.metadata({
      StudyInstanceUID: studyInstanceUID,
      madeInClient: true,
    });
  }

  const registeredStudyUIDs = useImportedStudyStore.getState().addStudies(studiesToRegister);

  notifyImportedStudiesAdded(registeredStudyUIDs);

  return registeredStudyUIDs;
}
